import {
  Button,
  IconPencil,
  IconTrash,
  Stack,
  Text,
  Tooltip,
} from '@vtex/admin-ui'
import { BudgetPermissions, useBudgetContext } from 'hooks'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { type BaseBudget } from 'typings'
import { ProgressBar } from '../../../../ProgressBar'
import { useNumbers } from '../../../../utils/useNumbers'
import { NotificationAlert } from '../components/NotificationAlert'
import { StopAlert } from '../components/StopAlert'
import { messages } from '../messages'
import { showTooltip } from '../shared/showTooltip'
import { styles } from '../styles'

type Props = {
  config: BaseBudget
  onAdd: () => void
  onRemove: () => void
}

export function BudgetView(props: Props) {
  const { config, onAdd, onRemove } = props

  const { $t } = useIntl()

  const { formatCurrency, formatPercentual } = useNumbers()

  const { amount, utilized } = config

  const percentage = useMemo(
    () => (amount ? (utilized / amount) * 100 : 0),
    [amount, utilized]
  )

  const { permission } = useBudgetContext()

  return (
    <Stack space="$space-3" fluid className={styles.hoverable}>
      <div className={styles.header}>
        <Text variant="title1">
          <FormattedMessage {...messages.title} />
        </Text>
        <div className={styles.actions}>
          <Tooltip
            text={$t(messages.unauthorizedWrite)}
            {...showTooltip(permission !== BudgetPermissions.WRITE)}
          >
            <div>
              <Button
                icon={<IconTrash />}
                variant="criticalTertiary"
                aria-label={$t(messages.delete)}
                onClick={onRemove}
                data-open={false}
                disabled={permission !== BudgetPermissions.WRITE}
              />
            </div>
          </Tooltip>
          <Tooltip
            text={$t(messages.unauthorizedWrite)}
            {...showTooltip(permission !== BudgetPermissions.WRITE)}
          >
            <div>
              <Button
                icon={<IconPencil />}
                variant="neutralTertiary"
                aria-label={$t(messages.edit)}
                onClick={onAdd}
                data-open={false}
                disabled={permission !== BudgetPermissions.WRITE}
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className={styles.content}>
        <Text variant="detail" tone="secondary">
          <FormattedMessage {...messages.totalSpent} />
        </Text>
        <Text
          className={styles.utilized}
          tone={utilized ? 'primary' : 'secondary'}
          variant="display"
        >
          {formatCurrency(utilized)}
        </Text>
        <ProgressBar percentage={percentage} />
        <div className={styles.legend}>
          <Text variant="detail" tone="secondary">
            {formatPercentual(percentage, 2)}
          </Text>
          <Text variant="body">{formatCurrency(amount)}</Text>
        </div>
      </div>
      <div className={styles.actions}>
        <NotificationAlert hide={!config.notification?.enable} />
        <StopAlert stop={percentage >= 100} />
      </div>
    </Stack>
  )
}
